// Qué parte de los filtros de «Mis ventas» viaja a `GET /sales?index=seller` y qué se queda sobre
// lo cargado, más los rótulos de las píldoras y los errores del listado. Puro.
import { ApiError } from '@/api/errors';
import type {
    SellerSalesCounters,
    SellerSalesFilterQuery,
} from '@/api/services/retail-wizard/contract';
import { mxDayEndUtc, mxDayStartUtc } from '@/lib/dateHelpers';
import {
    countWithNoun,
    loadedCountLabel,
    loadedCountTitle,
    SALES_NOUN,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import type { MySalesFilters, SalesSegment, SegmentCounts, SortState } from './mySalesFilters';

type SaleTypeParam = NonNullable<SellerSalesFilterQuery['type']>;

/** Tipo que fija cada píldora de financiera en el silo. */
const SEGMENT_TYPE: Partial<Record<SalesSegment, SaleTypeParam>> = {
    surekeep: 'CREDIT',
    cash: 'CASH',
    others: 'EXTERNAL_REFERRAL',
};

/** Tipo y proveedor que equivalen a la financiera del panel. */
function financierParams(financier: string): Pick<SellerSalesFilterQuery, 'type' | 'provider'> {
    if (financier === '') return {};
    if (financier === 'contado') return { type: 'CASH' };
    if (financier === 'SUREKEEP') return { type: 'CREDIT' };
    return { type: 'EXTERNAL_REFERRAL', provider: financier };
}

/** El chip «Crédito» es CREDIT o EXTERNAL_REFERRAL: el silo no acepta el OR y se filtra en el front. */
function chipType(types: MySalesFilters['types']): SaleTypeParam | undefined {
    return types.length === 1 && types[0] === 'contado' ? 'CASH' : undefined;
}

/**
 * Los filtros que el silo sabe aplicar. Con píldora de financiera el `type` del panel no viaja:
 * el silo responde 400 si chocan, y el filtro en memoria ya deja la tabla vacía.
 */
export function toSellerSalesFilterQuery(
    filters: MySalesFilters,
    segment: SalesSegment,
    sort: SortState,
): SellerSalesFilterQuery {
    const financier = financierParams(filters.financier);
    const type = financier.type ?? chipType(filters.types);
    const q = filters.query.trim();
    return {
        sortOrder: sort.key === 'soldAt' ? sort.direction : 'desc',
        ...(segment !== 'all' && { segment }),
        ...(filters.from !== '' && { from: mxDayStartUtc(filters.from) }),
        ...(filters.to !== '' && { to: mxDayEndUtc(filters.to) }),
        ...(type !== undefined && SEGMENT_TYPE[segment] === undefined && { type }),
        ...(financier.provider !== undefined && { provider: financier.provider }),
        ...(q !== '' && { q }),
    };
}

/** Filtros puestos que el silo no aplica y solo miran lo cargado. */
export function localOnlyFilterLabels(filters: MySalesFilters): string[] {
    const labels: string[] = [];
    if (filters.brand !== '') labels.push('producto');
    if (filters.types.length === 1 && filters.types[0] === 'credito') labels.push('tipo «Crédito»');
    if (filters.minAmount !== '' || filters.maxAmount !== '') labels.push('monto');
    return labels;
}

/** El número de una píldora; con el tope de lectura del silo es un mínimo. */
export function pillCountLabel(count: number, truncated: boolean): string {
    const formatted = count.toLocaleString('en-US');
    return truncated ? `${formatted}+` : formatted;
}

export interface MySalesListErrorCopy {
    title: string;
    description: string;
    /** Falso: repetir la misma consulta da el mismo 400. */
    canRetry: boolean;
}

const FILTER_ERROR_CODES = new Set(['SALE_LIST_FILTER_INVALID', 'VALIDATION_ERROR']);

/** Qué decir cuando la primera página no llega, según el `code` del silo. */
export function mySalesListErrorCopy(error: unknown): MySalesListErrorCopy {
    if (error instanceof ApiError && FILTER_ERROR_CODES.has(error.code)) {
        return {
            title: 'Revisa los filtros.',
            description:
                'Revisa que la fecha «desde» no sea posterior a «hasta» y vuelve a buscar.',
            canRetry: false,
        };
    }
    return {
        title: 'No se pudieron leer tus ventas.',
        description: 'Vuelve a intentarlo; si sigue igual, avísale a tu gerente.',
        canRetry: true,
    };
}

export interface PillCount {
    value: string;
    title: string;
}

/**
 * Número y explicación de cada píldora. Con `counters` es el total del servidor; sin ellos se
 * cuenta lo cargado y, si quedan páginas, se rotula como parcial.
 */
export function pillCounts(input: {
    counters: SellerSalesCounters | undefined;
    loadedCounts: SegmentCounts;
    scope: LoadedScope;
}): Record<SalesSegment, PillCount> {
    const { counters, loadedCounts, scope } = input;
    const segments = Object.keys(loadedCounts) as SalesSegment[];
    return Object.fromEntries(
        segments.map((segment) => [
            segment,
            counters
                ? {
                      value: pillCountLabel(counters[segment], counters.truncated),
                      title: counters.truncated
                          ? 'Total mínimo: tienes más ventas de las que se alcanzan a contar.'
                          : 'Total de tus ventas con los filtros de búsqueda, financiera, tipo y fecha.',
                  }
                : {
                      value: loadedCountLabel(loadedCounts[segment], scope),
                      title: loadedCountTitle('Cuenta las ventas cargadas', scope, SALES_NOUN),
                  },
        ]),
    ) as Record<SalesSegment, PillCount>;
}

/**
 * El aviso bajo las píldoras mientras queden páginas: qué mide solo lo cargado. `null` sin páginas
 * pendientes, porque entonces lo cargado es todo.
 */
export function loadedSalesNote(input: {
    scope: LoadedScope;
    localFilters: string[];
    localSort: boolean;
}): string | null {
    const { scope, localFilters, localSort } = input;
    if (!scope.hasMore) return null;
    const loaded = countWithNoun(scope.loaded, SALES_NOUN);
    const partial = [
        'ticket, volumen y % SK',
        ...localFilters.map((label) => `el filtro de ${label}`),
    ];
    if (localSort) partial.push('el orden por columna');
    return `Llevas ${loaded} cargadas. Las píldoras cuentan el total; ${joinEs(partial)} solo miran lo cargado. Usa «Cargar más» al pie de la tabla para traer el resto.`;
}

/** «a», «a y b», «a, b y c». */
function joinEs(items: string[]): string {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}
